import { useEffect, useRef, useState } from "react";
import { _ } from "i18n";
import { useVideoPlayer } from "../../../Contexts/contexts";
import { type EventFilterValue, type EventFilterView, FilterCategory, VideoSidebar } from "../../../types";
import { Badge } from "ui-kit";
import "./PlayerEventsFilterModal.css";
import type { Keys } from "i18n/locales/types";
import { Button, Popover } from "react-aria-components";
import clsx from "clsx";
import { useGameStore } from "../../../store";
import { useEditStore } from "../../../edit-store";
import { handlePlayEvent } from "../../../functions";
import { Play } from "lucide-react";

interface PlayerEventsFilterModalProps {
	playerId: number;
	clipsAllowed: boolean;
	disabled: boolean;
}

interface FilterOption {
	count: number;
	filterView: EventFilterView;
	selected: boolean;
}

export function PlayerEventsFilterModal({ playerId, clipsAllowed, disabled }: PlayerEventsFilterModalProps) {
	const { setFilterValues, setFilteredFilterValues, setSidebar, eventFilter } = useGameStore();
	const { setShowClipCreateModal } = useEditStore();
	const { playerRef } = useVideoPlayer();
	const triggerRef = useRef<HTMLDivElement>(null);
	const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
	const [isOpen, setIsOpen] = useState(false);

	const onClearFilter = () => {
		setFilterOptions(
			filterOptions.map((oi) => {
				oi.selected = false;
				return oi;
			})
		);
	};

	const onSelectAll = () => {
		setFilterOptions(
			filterOptions.map((oi) => {
				oi.selected = true;
				return oi;
			})
		);
	};

	const onToggleOne = (key: string) => {
		setFilterOptions(
			filterOptions.map((oi) => {
				oi.selected = oi.filterView.key === key ? !oi.selected : oi.selected;
				return oi;
			})
		);
	};

	const onPlayEvents = () => {
		const filterValues = createFilterValues();

		setFilterValues(filterValues);
		setFilteredFilterValues(filterValues);
		setSidebar(VideoSidebar.EventList);
		handlePlayEvent(playerRef);
	};

	const onPlayAllEvents = () => {
		setFilterOptions(
			filterOptions.map((oi) => {
				oi.selected = true;
				return oi;
			})
		);
		onPlayEvents();
	};

	const onCreateClip = () => {
		setShowClipCreateModal(true);
		onPlayEvents();
	};

	const onExpand = () => {
		const filterValues: EventFilterValue[] = eventFilter?.mutateFilterValuesByPlayerId([], playerId);

		setFilterValues(filterValues);
		setFilteredFilterValues(filterValues);
		setSidebar(VideoSidebar.Filters);
	};

	const createFilterValues = (): EventFilterValue[] => {
		let filterValues: EventFilterValue[] = [];
		// biome-ignore lint/complexity/noForEach: <explanation>
		filterOptions.forEach((fo) => {
			if (fo.selected) {
				filterValues = eventFilter?.mutateFilterValues(filterValues, fo.filterView.key, fo.filterView.value);
			}
		});

		filterValues = eventFilter?.mutateFilterValuesByPlayerId(filterValues, playerId);

		return filterValues;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const newFilterOptions: FilterOption[] = [];
		// biome-ignore lint/complexity/noForEach: <explanation>
		eventFilter?.flatFilterViews.forEach((fv) => {
			if (eventFilter.getFilterCategory(fv.key) === FilterCategory.Event) {
				const eventsCount = eventFilter.eventsCount(
					fv.key,
					[fv.value],
					[
						{ key: "player.home", value: [playerId.toString()] },
						{ key: "player.away", value: [playerId.toString()] },
					]
				);

				if (eventsCount > 0) {
					newFilterOptions.push({
						count: eventsCount,
						filterView: fv,
						selected: false,
					});
				}
			}
		});

		setFilterOptions(newFilterOptions);
	}, [eventFilter]);

	const selectedCount = filterOptions.filter((fo) => fo.selected).length;

	const filterOptionsSorted = filterOptions.sort((a, b) => {
		return b.count - a.count;
	});

	return (
		<div>
			<div ref={triggerRef}>
				<button
					type="button"
					className="team-sheet-actions__play-events"
					aria-label={_("label.filter_by_event(s)")}
					disabled={disabled}
					onClick={() => setIsOpen(!isOpen)}
				>
					<Play className="team-sheet-actions__play-events-icon" size={12} strokeWidth={2.5} aria-hidden />
				</button>
			</div>
			<Popover
				UNSTABLE_portalContainer={document.getElementById("video-sidebar") ?? undefined}
				triggerRef={triggerRef}
				className="modal-overlay"
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				placement="bottom end"
				containerPadding={16}
				shouldFlip={true}
				shouldUpdatePosition={true}
			>
				<div className="modal-header">
					{_("label.filter_by_event(s)")}
					<span className="modal-header__close" onClick={() => setIsOpen(false)}>
						<span className="icon-new icon-light-cross icon-14" />
					</span>
				</div>
				<div className="modal-body">
					{filterOptionsSorted.length !== 0 ? (
						<>
							<div className="modal-button-advance-filters">
								<Badge
									icon={"filter-new"}
									iconPosition={"right"}
									label={_("teamSheet.eventsModal.action.advancedFilters")}
									variant="default"
									outline={true}
									size="sm"
									onClick={onExpand}
								/>
							</div>
							<div className="modal-buttons">
								<Badge
									label={_("teamSheet.eventsModal.action.deselectAll")}
									variant="default"
									outline={true}
									size="sm"
									onClick={onClearFilter}
									disabled={selectedCount === 0}
								/>
								<Badge
									label={_("teamSheet.eventsModal.action.selectAll")}
									variant="default"
									outline={true}
									size="sm"
									onClick={onSelectAll}
									disabled={selectedCount === filterOptionsSorted.length}
								/>
							</div>
							<ul className="modal-list">
								{filterOptionsSorted.map((fo) => (
									<li key={fo.filterView.key} className="modal-list__item">
										<input
											type="checkbox"
											checked={fo.selected}
											onClick={(e) => e.stopPropagation()}
											id={`check-${fo.filterView.key}`}
											onChange={() => onToggleOne(fo.filterView.key)}
										/>
										<label htmlFor={`check-${fo.filterView.key}`}>
											{_(fo.filterView.label as keyof Keys)} ({fo.count})
										</label>
									</li>
								))}
							</ul>
							<div className="modal-controls">
								{clipsAllowed && (
									<Button
										className={clsx("modal-button", { disabled: selectedCount === 0 })}
										onPress={onCreateClip}
										isDisabled={selectedCount === 0}
									>
										{_("teamSheet.eventsModal.action.createClip")}
									</Button>
								)}
								<Button className={clsx("modal-button", "playEvents")} onPress={selectedCount === 0 ? onPlayAllEvents : onPlayEvents}>
									<span className="icon-new icon-play icon-white icon-10" />
									{selectedCount === 0
										? _("teamSheet.eventsModal.action.playEventsAll")
										: _("teamSheet.eventsModal.action.playEvents")}
								</Button>
							</div>
						</>
					) : (
						<div className="modal-empty">{_("teamSheet.eventsModal.nothingToPlay")}</div>
					)}
				</div>
			</Popover>
		</div>
	);
}
